/**
 * Popup Modal
 * Auto-opens a promo popup on page load only when the reappear window has elapsed
 * since the last dismiss (localStorage timestamp). X / overlay / Esc close it, write
 * the timestamp, and lock body scroll while open.
 */

const STORAGE_PREFIX = 'popup-modal:dismissed:';

function shouldShow(key, reappearHours) {
  // reappearHours <= 0 => always show on load
  if (!reappearHours || reappearHours <= 0) return true;
  let last;
  try {
    last = window.localStorage.getItem(STORAGE_PREFIX + key);
  } catch (e) {
    // Private mode / storage blocked — fail open so the promo still shows.
    return true;
  }
  if (!last) return true;
  const elapsedMs = Date.now() - Number(last);
  return elapsedMs >= reappearHours * 60 * 60 * 1000;
}

function getFocusable(modal) {
  return [...modal.querySelectorAll('a[href], button:not([disabled])')].filter(
    (el) => el.offsetParent !== null
  );
}

function open(modal) {
  modal._lastFocused = document.activeElement;
  modal.hidden = false;
  modal.classList.add('is-open');
  document.body.style.overflow = 'hidden';
  const first = getFocusable(modal)[0];
  if (first) first.focus({ preventScroll: true });
}

function close(modal) {
  modal.classList.remove('is-open');
  modal.hidden = true;
  document.body.style.overflow = '';
  if (modal._lastFocused && modal._lastFocused.focus) modal._lastFocused.focus();
  try {
    window.localStorage.setItem(STORAGE_PREFIX + modal.dataset.storageKey, String(Date.now()));
  } catch (e) {
    // Storage blocked — nothing to persist; popup reappears next load.
  }
}

// Keep Tab focus inside the open modal instead of escaping to the page behind.
function trapTab(modal, e) {
  if (e.key !== 'Tab' || !modal.classList.contains('is-open')) return;
  const focusable = getFocusable(modal);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  if (!modal.contains(active)) {
    e.preventDefault();
    first.focus();
  } else if (e.shiftKey && active === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

function setupModal(modal) {
  if (modal.dataset._popupAttached) return;
  modal.dataset._popupAttached = 'true';

  const reappearHours = Number(modal.dataset.reappearHours);

  modal.querySelectorAll('[data-popup-modal-close]').forEach((el) => {
    el.addEventListener('click', () => close(modal));
  });

  // Esc closes + trap Tab within the modal — bound on document so keys are caught
  // regardless of which element (or the page behind) currently holds focus.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('is-open')) close(modal);
    trapTab(modal, e);
  });

  if (shouldShow(modal.dataset.storageKey, reappearHours)) {
    open(modal);
  }
}

export function initPopupModal(root = document) {
  root.querySelectorAll('[data-popup-modal]').forEach(setupModal);
}

export default initPopupModal;
